import { ActionTree, GetterTree, MutationTree } from "vuex";
import { NolaCoinClient } from "@/lib/clients/nolaCoinClient";
import { Wallet } from "@/lib/models/nolaCoin";
import { logError } from "@/lib/utils/logger";

// Fish関連の型定義
export interface FishData {
  currentFish: number;
  maxFish: number;
  lastUpdated: Date;
}

// State型定義
export interface FishState {
  currentFish: number; // 現在のFish数
  maxFish: number; // 最大Fish数（月間上限など）
  isLoading: boolean; // ローディング状態
  error: string | null; // エラーメッセージ
  lastUpdated: Date | null; // 最終更新日時
}

// Mutations型定義
interface Mutations {
  setCurrentFish(state: FishState, fish: number): void;
  setMaxFish(state: FishState, fish: number): void;
  setFishData(state: FishState, data: FishData): void;
  subtractFish(state: FishState, amount: number): void;
  setLoading(state: FishState, loading: boolean): void;
  setError(state: FishState, error: string | null): void;
  setLastUpdated(state: FishState, date: Date): void;
  resetState(state: FishState): void;
}

// Actions型定義
interface Actions {
  fetchFishData(context: any): Promise<void>;
  consumeFishForRequest(context: any, amount: number): Promise<boolean>;
  resetFishData(context: any): Promise<void>;
}

// Getters型定義
interface Getters {
  currentFish(state: FishState): number;
  maxFish(state: FishState): number;
  remainingFish(state: FishState): number;
  fishUsagePercentage(state: FishState): number;
  canConsumeFish(state: FishState): (amount: number) => boolean;
  isLoading(state: FishState): boolean;
  error(state: FishState): string | null;
  lastUpdated(state: FishState): Date | null;
  formattedCurrentFish(state: FishState): string;
  formattedMaxFish(state: FishState): string;
}

// Mutations
const mutations: MutationTree<FishState> & Mutations = {
  setCurrentFish(state, fish) {
    state.currentFish = Math.max(0, fish); // 負の値を防ぐ
    state.lastUpdated = new Date();
  },
  setMaxFish(state, fish) {
    state.maxFish = Math.max(0, fish); // 負の値を防ぐ
    state.lastUpdated = new Date();
  },
  setFishData(state, data) {
    state.currentFish = Math.max(0, data.currentFish);
    state.maxFish = Math.max(0, data.maxFish);
    state.lastUpdated = data.lastUpdated;
  },

  subtractFish(state, amount) {
    const newAmount = state.currentFish - amount;
    state.currentFish = Math.max(0, newAmount); // 負の値を防ぐ
    state.lastUpdated = new Date();
  },
  setLoading(state, loading) {
    state.isLoading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  setLastUpdated(state, date) {
    state.lastUpdated = date;
  },
  resetState(state) {
    state.currentFish = 0;
    state.maxFish = 0; // API連携時は0からスタート
    state.isLoading = false;
    state.error = null;
    state.lastUpdated = null;
  },
};

// Actions
const actions: ActionTree<FishState, {}> & Actions = {
  async fetchFishData({ commit }) {
    try {
      commit("setLoading", true);
      commit("setError", null);

      // NolaCoin API からウォレット情報を取得
      const nolaCoinClient = new NolaCoinClient();
      const wallet: Wallet = await nolaCoinClient.getWallet();

      // Wallet データを Fish データにマッピング
      const fishData: FishData = {
        currentFish: wallet.amount, // Wallet残高 → 現在のFish数
        maxFish: wallet.totalAmount, // 総残高 → 最大Fish数として使用
        lastUpdated: new Date(),
      };

      commit("setFishData", fishData);
    } catch (error) {
      logError({ module: "Fish", action: "fetchFishData" }, "NolaCoin API エラー", error);

      // API エラー時はフォールバック用のモックデータを使用
      const fallbackData: FishData = {
        currentFish: 0,
        maxFish: 0,
        lastUpdated: new Date(),
      };

      commit("setFishData", fallbackData);
      commit("setError", "Fish データの取得に失敗しました。オフラインモードで表示しています。");
    } finally {
      commit("setLoading", false);
    }
  },

  async consumeFishForRequest({ commit, state }, amount) {
    try {
      if (state.currentFish < amount) {
        commit("setError", "Fish が不足しています");
        return false;
      }

      commit("setError", null);
      commit("subtractFish", amount);
      return true;
    } catch (error) {
      commit("setError", "Fish の消費に失敗しました");
      return false;
    }
  },

  async resetFishData({ commit }) {
    commit("resetState");
  },
};

// Getters
const getters: GetterTree<FishState, {}> & Getters = {
  currentFish: (state) => state.currentFish,
  maxFish: (state) => state.maxFish,
  remainingFish: (state) => state.maxFish - state.currentFish,
  fishUsagePercentage: (state) => {
    if (state.maxFish === 0) return 0;
    return Math.round((state.currentFish / state.maxFish) * 100);
  },
  canConsumeFish: (state) => (amount: number) => state.currentFish >= amount,
  isLoading: (state) => state.isLoading,
  error: (state) => state.error,
  lastUpdated: (state) => state.lastUpdated,
  formattedCurrentFish: (state) => state.currentFish.toLocaleString("ja-JP"),
  formattedMaxFish: (state) => state.maxFish.toLocaleString("ja-JP"),
};

// 初期状態
const state: FishState = {
  currentFish: 0,
  maxFish: 0, // API連携により動的取得
  isLoading: false,
  error: null,
  lastUpdated: null,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
